import { useEffect, useRef } from 'react';

import { MecProduct } from '@adapters/bigcommerce/types/product';
import { usePdp } from '@context/PdpContext';

interface UsePdpSelectionFromQueryParams {
  path: string;
  product: MecProduct;
}

export const usePdpSelectionFromQuery = ({
  path,
  product,
}: UsePdpSelectionFromQueryParams) => {
  const {
    colours,
    sizes,
    setSelectedColourId,
    setSelectedSizeId,
    resetStateWithSizeAndColour,
  } = usePdp();

  const firstFiredFlags = useRef<Array<'size' | 'colour' | 'nextLink'>>([]);

  useEffect(() => {
    const availableSizes =
      sizes?.filter(
        (size) =>
          typeof size.notAvailableForSelectedColour !== 'undefined' &&
          !size.notAvailableForSelectedColour
      ) ?? [];

    if (!availableSizes.length || firstFiredFlags.current.includes('size')) {
      return;
    }

    const url = new URL(window.location.href);
    const sizeName = url.searchParams.get('size') ?? null;

    const sizeId = availableSizes.find((size) => size.name === sizeName)?.id;

    if (typeof sizeId !== 'undefined') {
      setSelectedSizeId(sizeId);
    }

    firstFiredFlags.current.push('size');
  }, [sizes, setSelectedSizeId]);

  useEffect(() => {
    if (!colours?.length || firstFiredFlags.current.includes('colour')) {
      return;
    }

    const url = new URL(window.location.href);
    const colourName = url.searchParams.get('colour') ?? null;
    const colourId =
      colours.find((colour) => colour.name === colourName)?.id ?? colours[0].id;

    setSelectedColourId(colourId);
    firstFiredFlags.current.push('colour');
  }, [colours, setSelectedColourId]);

  useEffect(() => {
    if (firstFiredFlags.current.includes('nextLink')) {
      return;
    }

    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    const nextLink = params.get('nextLink') ?? null;
    const colourId = params.get('colourId') ?? null;
    const sizeId = params.get('sizeId') ?? null;

    if (nextLink !== null && colourId !== null && sizeId !== null) {
      const size = parseInt(sizeId);
      const colour = parseInt(colourId);
      if (Number.isFinite(size) && Number.isFinite(colour)) {
        resetStateWithSizeAndColour(size, colour);
        window.history.replaceState(null, '', path);
      }
    }

    firstFiredFlags.current.push('nextLink');
    // Only depend on path and product id to avoid re-running as PDP state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, product?.entityId, resetStateWithSizeAndColour]);
};
